import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  Drone, 
  Eye, 
  MapPin, 
  Shield,
  CheckCircle,
  Users,
  Clock,
  Radio,
  Camera,
  Zap
} from 'lucide-react';

const DroneServicesPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const services = [
    {
      title: "Aerial Reconnaissance Missions",
      description: "Strategic aerial intelligence gathering using advanced drone platforms with high-resolution imaging and real-time data transmission.",
      icon: Eye,
      image: "https://images.pexels.com/photos/7663144/pexels-photo-7663144.jpeg",
      features: [
        "High-altitude surveillance flights",
        "Multi-spectral imaging capabilities",
        "Real-time intelligence gathering",
        "Covert reconnaissance operations",
        "Border monitoring and patrol",
        "Asset tracking and verification"
      ]
    },
    {
      title: "Surveillance & Monitoring Operations",
      description: "Continuous aerial monitoring for security threats, crowd control, and perimeter protection using advanced drone technology.",
      icon: Camera,
      image: "https://images.unsplash.com/photo-1581488066648-56a585ab6767",
      features: [
        "24/7 perimeter monitoring",
        "Crowd surveillance and analysis",
        "Event security overwatch",
        "Critical infrastructure protection",
        "Real-time threat assessment",
        "Emergency response support"
      ]
    },
    {
      title: "Certified Drone Pilot Services",
      description: "Professional drone operations conducted by FAA Part 107 certified pilots with specialized training in government operations.",
      icon: Users,
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      features: [
        "FAA Part 107 certified pilots",
        "Security clearance available",
        "Specialized tactical training",
        "Government operation experience",
        "Emergency response certified",
        "Multi-platform expertise"
      ]
    },
    {
      title: "Tactical Flight Operations",
      description: "Specialized drone missions for law enforcement, military, and intelligence agencies requiring tactical precision and security.",
      icon: Shield,
      image: "https://images.pexels.com/photos/7688543/pexels-photo-7688543.jpeg",
      features: [
        "Tactical mission planning",
        "Stealth operation capabilities",
        "Night vision and thermal imaging",
        "Encrypted communication systems",
        "Rapid deployment protocols",
        "Multi-drone coordination"
      ]
    }
  ];

  const droneCapabilities = [
    {
      title: "Advanced Sensor Systems",
      description: "Multi-spectral cameras, thermal imaging, LIDAR, and specialized sensors",
      icon: Camera
    },
    {
      title: "Secure Communications",
      description: "Encrypted data links and secure command and control systems",
      icon: Radio
    },
    {
      title: "Extended Flight Time",
      description: "Long-endurance platforms capable of multi-hour missions",
      icon: Clock
    },
    {
      title: "Rapid Deployment",
      description: "Quick response capabilities for emergency and tactical situations",
      icon: Zap
    }
  ];

  const missionTypes = [
    "Border Security Patrol",
    "Critical Infrastructure Monitoring",
    "Search and Rescue Operations",
    "Disaster Assessment and Response",
    "Counter-Surveillance Operations",
    "Intelligence Gathering Missions",
    "Event Security Overwatch",
    "Emergency Response Support"
  ];

  const droneSpecs = [
    {
      category: "Flight Performance",
      specs: [
        "Maximum altitude: 400+ feet AGL",
        "Flight time: Up to 8 hours",
        "Wind resistance: 40+ mph",
        "Operating range: 50+ miles",
        "Payload capacity: 20+ lbs",
        "All-weather operation capability"
      ]
    },
    {
      category: "Imaging & Sensors",
      specs: [
        "4K Ultra HD video recording",
        "Thermal imaging (FLIR certified)",
        "Night vision capabilities",
        "Multi-spectral imaging",
        "LIDAR mapping systems",
        "Real-time image transmission"
      ]
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="section-padding relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/7663144/pexels-photo-7663144.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-near-black/90 z-0" />
        <div className="absolute inset-0 grid-pattern opacity-20 z-1" />
        
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-16 flex items-center justify-center">
                <Drone className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="heading-font text-4xl md:text-6xl font-bold text-off-white">
                  Drone Operations & <span className="text-royal-gold">Services</span>
                </h1>
              </div>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed max-w-3xl">
              Professional drone services with certified pilots for reconnaissance, surveillance, 
              and intelligence missions. Advanced aerial platforms supporting government operations, 
              emergency response, and critical infrastructure protection.
            </p>

          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Professional <span className="text-royal-gold">Drone Services</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Advanced unmanned aerial vehicle operations with certified pilots and 
              cutting-edge technology for government, intelligence, and emergency response missions.
            </p>
          </motion.div>

          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-16 flex items-center justify-center">
                      <service.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features List */}
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-royal-gold flex-shrink-0" />
                        <span className="text-gray-200">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-12 flex items-center justify-center">
                        <service.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Drone Capabilities */}
      <section className="section-padding bg-near-black">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Advanced <span className="text-royal-gold">Capabilities</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {droneCapabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-morphism p-6 rounded-16 text-center hover:border-royal-gold transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-royal-gold to-gold-light rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <capability.icon className="w-8 h-8 text-near-black" />
                </div>
                <h3 className="text-lg font-semibold text-off-white mb-2 group-hover:text-royal-gold transition-colors duration-300">
                  {capability.title}
                </h3>
                <p className="text-gray-300 text-sm">
                  {capability.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="section-padding bg-gray-dark">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
              Technical <span className="text-royal-gold">Specifications</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Military-grade drone platforms with advanced sensors and communication systems 
              designed for professional government and intelligence operations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {droneSpecs.map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card"
              >
                <h3 className="heading-font text-2xl font-semibold text-royal-gold mb-6">
                  {spec.category}
                </h3>
                <div className="space-y-3">
                  {spec.specs.map((item, itemIndex) => (
                    <div key={itemIndex} className="flex items-center space-x-3">
                      <CheckCircle className="w-4 h-4 text-royal-gold flex-shrink-0" />
                      <span className="text-gray-200 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Types */}
      <section className="section-padding bg-near-black">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Mission <span className="text-royal-gold">Types</span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Our drone operations support a wide range of government and emergency response 
                missions, providing critical aerial intelligence and real-time situational awareness.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {missionTypes.map((mission, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <MapPin className="w-5 h-5 text-royal-gold flex-shrink-0" />
                    <span className="text-gray-200">{mission}</span>
                  </div>
                ))}
              </div>

            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="glass-morphism p-8 rounded-24">
                <div className="flex items-center space-x-4 mb-6">
                  <Users className="w-8 h-8 text-royal-gold" />
                  <h3 className="text-2xl font-semibold text-off-white">Certified Pilots</h3>
                </div>
                <p className="text-gray-300">
                  Our drone pilots hold FAA Part 107 certifications and specialized training 
                  for government operations, with security clearances available for classified missions.
                </p>
              </div>

              <div className="glass-morphism p-8 rounded-24">
                <div className="flex items-center space-x-4 mb-6">
                  <Clock className="w-8 h-8 text-royal-gold" />
                  <h3 className="text-2xl font-semibold text-off-white">24/7 Availability</h3>
                </div>
                <p className="text-gray-300">
                  Emergency response teams available around the clock for urgent missions, 
                  with rapid deployment capabilities nationwide.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DroneServicesPage;